import { useState } from 'react';
import { Control, Controller, FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import {
  cn,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Field,
  FieldContent,
  FieldError,
  FieldLabel
} from '@bliks-io/ui';
import type { Department, BulkValidateDepartmentInput } from '@bliks-io/contracts';
import { useFieldErrorMessage } from '@/components/form/rhf';

interface ParentAbbreviationComboBoxProps {
  control: Control<BulkValidateDepartmentInput>;
  name: FieldPath<BulkValidateDepartmentInput>;
  label: string;
  placeholder?: string;
  departments: Department[];
  batchAbbreviations: string[]; // Abbreviations from current batch
  required?: boolean;
}

export function ParentAbbreviationComboBox({
  control,
  name,
  label,
  placeholder = 'Select parent...',
  departments,
  batchAbbreviations,
  required = false
}: ParentAbbreviationComboBoxProps) {
  const { t } = useTranslation();
  const fieldErrorMessage = useFieldErrorMessage();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedDepartment = departments.find((dept) => dept.abbreviation === field.value);

        let displayValue = '';
        if (field.value) {
          if (selectedDepartment) {
            displayValue = `${selectedDepartment.name} (${selectedDepartment.abbreviation})`;
          } else {
            displayValue = field.value; // Show the typed abbreviation if no match
          }
        }

        // Filter departments by search value
        const filteredDepartments = departments.filter(
          (dept) =>
            dept.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            dept.abbreviation.toLowerCase().includes(searchValue.toLowerCase())
        );

        // Filter batch abbreviations by search value
        const filteredBatchAbbrs = batchAbbreviations.filter((abbr) =>
          abbr.toLowerCase().includes(searchValue.toLowerCase())
        );

        const handleClear = () => {
          field.onChange(undefined);
          setSearchValue('');
        };

        return (
          <Field>
            <FieldContent>
              <FieldLabel>
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
              </FieldLabel>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                      'w-full justify-between',
                      !field.value && 'text-muted-foreground'
                    )}
                  >
                    <span className="truncate">{displayValue || placeholder}</span>
                    <div className="flex items-center gap-1">
                      {field.value && (
                        <X
                          className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClear();
                          }}
                        />
                      )}
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={t('common.search')}
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                    <CommandEmpty>
                      {searchValue ? (
                        <div className="p-2">
                          <p className="text-sm text-muted-foreground mb-2">
                            {t('departmentSelect.noResults')}
                          </p>
                          <Button
                            variant="ghost"
                            size="small"
                            className="w-full justify-start"
                            onClick={() => {
                              field.onChange(searchValue.toUpperCase());
                              setOpen(false);
                              setSearchValue('');
                            }}
                          >
                            {t('organization.departments.bulkUploadPage.useBatchAbbreviation', {
                              abbreviation: searchValue.toUpperCase()
                            })}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-2">
                          {t('departmentSelect.noResults')}
                        </p>
                      )}
                    </CommandEmpty>

                    {/* Existing Departments from Database */}
                    {filteredDepartments.length > 0 && (
                      <CommandGroup
                        heading={t('organization.departments.bulkUploadPage.existingDepartments')}
                      >
                        {filteredDepartments.map((dept) => (
                          <CommandItem
                            key={dept._id}
                            value={dept.abbreviation}
                            onSelect={() => {
                              field.onChange(dept.abbreviation);
                              setOpen(false);
                              setSearchValue('');
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                field.value === dept.abbreviation ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-positive">🟢</span>
                              <span>
                                {dept.name}{' '}
                                <span className="text-muted-foreground">({dept.abbreviation})</span>
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    {/* Batch Abbreviations (from current upload) */}
                    {filteredBatchAbbrs.length > 0 && (
                      <CommandGroup
                        heading={t('organization.departments.bulkUploadPage.batchDepartments')}
                      >
                        {filteredBatchAbbrs.map((abbr) => (
                          <CommandItem
                            key={abbr}
                            value={abbr}
                            onSelect={() => {
                              field.onChange(abbr);
                              setOpen(false);
                              setSearchValue('');
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                field.value === abbr ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-info">🔵</span>
                              <span className="font-mono">{abbr}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
              {fieldState.error && <FieldError>{fieldErrorMessage(fieldState.error)}</FieldError>}
            </FieldContent>
          </Field>
        );
      }}
    />
  );
}
